import type { APIGatewayProxyEventV2WithRequestContext, Context } from 'aws-lambda'

export interface CustomAuthorizerContext {
	user?: {
		userId: string
		email: string
		role: UserRole
		providerId?: string
	}
}

export type CustomAPIGatewayProxyEventV2 = APIGatewayProxyEventV2WithRequestContext<{
	authorizer: CustomAuthorizerContext
}>

export type ApiHandler = (event: CustomAPIGatewayProxyEventV2, context: Context) => Promise<ApiResponse>

export interface ApiResponse {
	statusCode: number
	body: string
	headers?: Record<string, string>
}

export type ApiRoutesMap = {
	GET?: Record<string, ApiHandler>
	POST?: Record<string, ApiHandler>
	PUT?: Record<string, ApiHandler>
	PATCH?: Record<string, ApiHandler>
	DELETE?: Record<string, ApiHandler>
	OPTIONS?: Record<string, ApiHandler>
}

export enum UserRole {
	SUPER_ADMIN = 'SUPER_ADMIN',
	ADMIN = 'ADMIN',
	USER = 'USER',
	VIEWER = 'VIEWER',
}

/**
 * Who is asking, as the two facts an authorization decision needs.
 *
 * Deliberately NOT the authorizer's claim object, the session, or anything else
 * request-shaped. A service that takes a `Principal` depends on "requests have a
 * caller", which stays true across transports; one that takes an API Gateway event
 * depends on running behind API Gateway. That difference is the whole point, because
 * a service passing this around is portable and one reading an ambient request
 * context is not.
 *
 * Read once at the controller and passed down as a parameter. Reaching for it from
 * module state further in would reintroduce exactly the coupling it avoids.
 */
export interface Principal {
	userId: string
	role: UserRole
}

/**
 * What a person DOES on the Content Factory — deliberately separate from
 * {@link UserRole}, which is what they may do. A PM can hold ADMIN rights and a
 * designer USER rights without either implying the other, so conflating the two
 * would force a permission change every time someone's craft changed.
 *
 * Today this only labels and groups people pickers (who is a designer, who is a
 * PM). It is the field permissions and routing would later key off, which is why
 * it is a stored enum rather than a display string.
 *
 * `UNASSIGNED` is the backfill value: a person whose function nobody has stated
 * yet, distinct from a person who genuinely has none.
 */
export enum JobFunction {
	UNASSIGNED = 'UNASSIGNED',
	PM = 'PM',
	DESIGNER = 'DESIGNER',
	COPYWRITER = 'COPYWRITER',
	REVIEWER = 'REVIEWER',
	ACCOUNT = 'ACCOUNT',
	DEVELOPER = 'DEVELOPER',
}

export interface QueryParams {
	page?: number
	limit?: number
	sort?: string
	order?: 'asc' | 'desc'
	filter?: Record<string, string | string[]>
	expand?: string[]
	fields?: string[]
}

export interface QueryConfig {
	ALLOWED_FIELDS: Set<string>
	ALLOWED_FILTERS: Set<string>
	ALLOWED_EXPANDS: Set<string>
	ALLOWED_SORT_FIELDS: Set<string>
	DEFAULT_PAGE: number
	DEFAULT_LIMIT: number
	MAX_LIMIT: number
}

export type CustomHandler<TEvent = CustomAPIGatewayProxyEventV2, TResult = ApiResponse> = (
	event: TEvent,
	context: Context,
) => Promise<TResult>
